import { Image, Share2 } from 'lucide-react';
import { useState } from 'react';
import { useDashboard } from '@/hooks/useDashboard';
import { useT } from '@/i18n/useT';
import { formatMoney } from '@/lib/format';
import { shareText } from '@/lib/share';
import { buildStoryCard, shareImage } from '@/lib/share-card';

/**
 * "Share my progress" — the user's real numbers as text through the OS share
 * sheet (WhatsApp, etc.) and as a generated Instagram-story image. Only shown
 * once there's a streak to be proud of (past the Día D); before that "X days
 * smoke-free" wouldn't mean anything.
 */
export function ShareButton({ className = '' }: { className?: string }) {
  const t = useT();
  const { data } = useDashboard();
  const [busy, setBusy] = useState(false);
  // Nothing to share before the Día D or while still smoking (no streak yet).
  if (!data || data.isReductionPhase || data.mode === 'smoking') return null;

  const days = data.streak.cleanDays;
  const money = formatMoney(data.savings.totalCents, data.savings.currency);

  const onShareText = () =>
    void shareText(
      t('share.text', {
        days,
        money,
        cigs: data.avoided.total,
        cravings: data.cravings.resistedTotal,
      }),
    );

  const onShareImage = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const blob = await buildStoryCard({
        days,
        savings: money,
        avoided: String(data.avoided.total),
        cravings: String(data.cravings.resistedTotal),
        labels: {
          tagline: t('share.card.tagline'),
          days: t(days === 1 ? 'share.card.days.one' : 'share.card.days.other'),
          saved: t('share.card.saved'),
          avoided: t('share.card.avoided'),
          cravings: t('share.card.cravings'),
        },
      });
      await shareImage(blob, 'respiria.png', 'respiria.isaies.com');
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className={`flex gap-2 ${className}`}>
      <button
        type="button"
        onClick={onShareText}
        className="flex min-h-11 flex-1 items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-surface-2 text-[15px] font-semibold text-content transition-transform active:scale-[0.99]"
      >
        <Share2 size={17} aria-hidden />
        {t('share.button')}
      </button>
      <button
        type="button"
        onClick={onShareImage}
        disabled={busy}
        aria-label={t('share.imageButton')}
        className="flex min-h-11 items-center justify-center gap-2 rounded-[var(--radius-btn)] bg-surface-2 px-4 text-[15px] font-semibold text-content transition-transform active:scale-[0.99] disabled:opacity-60"
      >
        <Image size={17} aria-hidden />
      </button>
    </div>
  );
}
